/** Stable identifiers emitted by the backend's ordered verdict rules. */
export type AiVerdict = 'ai' | 'likely-ai' | 'mostly-ai' | 'mixed' | 'uncertain' | 'mostly-human' | 'likely-human' | 'human';
export interface AiMetrics {
    verdict?: AiVerdict;
    probabilities?: Record<string, number>;
    composition_available?: boolean;
}

/** The five classes of the AI scale, human first: a sentence's highlight, a score's colour. */
export const AI_TONES = ['human', 'human-low', 'uncertain', 'ai-low', 'ai'] as const;
export type AiTone = typeof AI_TONES[number];

/** Which scale a 0..1 score is read on. */
export type ScoreKind = 'ai' | 'plagiarism';
/** What a verdict pill shows: a 0..1 score on its scale, or grammar's issue count. */
export type VerdictKind = ScoreKind | 'grammar';

// Lower bounds (0..1) of every class but the first. The backend splits sentences
// (ai_metrics.sentence_tone), the PDF bands and Canvas on the same numbers.
const AI_BOUNDS = [.25, .4, .6, .75];
// Plagiarism has three bands — Original / Mixed / Plagiarism — painted in the AI
// scale's human / probably-AI / AI colours, as on the PDF certificate.
const PLAGIARISM_BOUNDS = [.35, .65];
const PLAGIARISM_TONES: AiTone[] = ['human', 'ai-low', 'ai'];

const band = (score: number, bounds: number[]) => bounds.filter(bound => score >= bound).length;

/** The class of a raw 0..1 score, unrounded — a sentence's or a whole document's. */
export function scoreTone(score: number, kind: ScoreKind = 'ai'): AiTone {
    return kind === 'ai' ? AI_TONES[band(score, AI_BOUNDS)] : PLAGIARISM_TONES[band(score, PLAGIARISM_BOUNDS)];
}

/** The class of a score as displayed: a whole percent, so a shown "75" is never painted as 74. */
export function shownTone(score: number, kind: ScoreKind = 'ai'): AiTone {
    return scoreTone(Math.round(score * 100) / 100, kind);
}

/** 1..5 for the `score-level-N` class that paints a displayed score in its class's colour (globals.scss). */
export function scoreLevel(score: number, kind: ScoreKind = 'ai'): number {
    return AI_TONES.indexOf(shownTone(score, kind)) + 1;
}

/** Grammar has no score scale: a few issues are normal writing, a pageful is not.
 *  Light green below 5 issues, yellow through 10, red from 11. */
const GRAMMAR_YELLOW_FROM = 5;
const GRAMMAR_RED_FROM = 11;

export const grammarTone = (issues: number): AiTone =>
    issues >= GRAMMAR_RED_FROM ? 'ai' : issues >= GRAMMAR_YELLOW_FROM ? 'ai-low' : 'human-low';

/** `scoreLevel` of a verdict pill — a grammar one counts issues. */
export function verdictLevel(value: number, kind: VerdictKind = 'ai'): number {
    return kind === 'grammar' ? AI_TONES.indexOf(grammarTone(value)) + 1 : scoreLevel(value, kind);
}

/** Presentation family of a document verdict, including neutral uncertainty. */
export function verdictTone(verdict: AiVerdict): string {
    if (verdict === 'human' || verdict.endsWith('-human')) return 'human';
    if (verdict === 'ai' || verdict.endsWith('-ai')) return 'ai';
    return verdict;
}
